import type { Layout } from '../../../data/types';
import type { PolygonIndex } from '../../../geo/polygonIndex';
import { obbCorners } from '../../../geo/polygon';
import type { Lot, ReservedFootprint } from '../types';
import { lotsOverlap } from '../lotFootprint';
import { lotFrame } from '../lotFrame';
import { obbFromFrontEdge } from '../lotObb';
import { createLotPlacer } from '../lotPlacer';
import { surveyedRowLots } from './rowLots';
import type { SurveyedRow } from './rowTypes';

/** Five visible small gables; widths, depths and height are estimates, not measured elevations. */
export const OUDEBRUG_ALLEY_ROW: SurveyedRow = {
  id: 'oudebrug-alley-five',
  source: 'street-oudebrugsteeg-2',
  side: 'right',
  from: 16,
  to: 44,
  depth: 7.5,
  evidence: 'painted',
  houses: Array.from({ length: 5 }, (_, i) => ({
    id: `oudebrug-alley-five-${i + 1}`,
    widthWeight: [1, 0.92, 1.08, 0.95, 1.05][i]!,
    storeys: 1,
    roof: i === 2 ? ('slate' as const) : ('tile' as const),
  })),
};
export const OUDEBRUG_QUAY = 'damrak-west-oudebrug-nieuwebrug-01';

/** The painted alley row takes precedence over an estimated deep rear wall, retaining its front. */
export function fitOudebrugAlley(
  lots: readonly Lot[],
  layout: Layout,
  buildable: PolygonIndex,
  reserved: readonly ReservedFootprint[]
): readonly Lot[] {
  const quay = lots.find((lot) => lot.reconstruction?.id === OUDEBRUG_QUAY);
  if (!quay || quay.plot) return lots;
  const cottages = surveyedRowLots([OUDEBRUG_ALLEY_ROW], layout, buildable, reserved, 12_000).map(
    (lot) => ({
      ...lot,
      reconstruction: {
        ...lot.reconstruction!,
        params: {
          ...lot.reconstruction!.params,
          frontType: 'timber' as const,
          frontGable: 'plain' as const,
          backGable: 'plain' as const,
          storeyHeight: 2.7,
          pitch: Math.PI / 3,
        },
      },
    })
  );
  if (cottages.length !== 5) return lots;
  const fitted = new Map<Lot, Lot>();
  for (const lot of lots) {
    if (!cottages.some((cottage) => lotsOverlap(cottage, lot, 0.001))) continue;
    // Preserve every frontage. Only the two neighboring estimated rear depths may shorten.
    if (
      ![OUDEBRUG_QUAY, 'nieuwendijk-oudebrug-karnemelk-07'].includes(
        lot.reconstruction?.id ?? ''
      ) ||
      lot.plot
    )
      return lots;
    const f = lotFrame(lot);
    let shortened = lot;
    for (let depth = lot.depth; depth >= 6; depth -= 0.25) {
      const point = (sign: number) =>
        [
          f.frontMid[0] + (f.right[0] * f.width * sign) / 2,
          f.frontMid[1] + (f.right[1] * f.width * sign) / 2,
        ] as const;
      const obb = obbFromFrontEdge(point(-1), point(1), depth, f.facing);
      shortened = { ...lot, obb, corners: obbCorners(obb), depth };
      if (!cottages.some((cottage) => lotsOverlap(cottage, shortened, 0.001))) break;
    }
    fitted.set(lot, shortened);
  }
  const kept = lots.map((lot) => fitted.get(lot) ?? lot);
  const placer = createLotPlacer((p) => buildable.contains(p), kept, reserved, buildable);
  for (const cottage of cottages) {
    if (!placer.fits(cottage.obb)) return lots;
    placer.add(cottage);
  }
  return [...kept, ...cottages].map((lot, index) => ({ ...lot, index }));
}
